import { ActivatedRoute, Router } from '@angular/router';
import { SwapSuccessDialogComponent } from './../swap-success-dialog/swap-success-dialog.component';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CryptoService } from '../../../abstraction/crypto.service';
import { ErrorHandlingService } from '../../../services/utils/error-handling.service';
import { AuthService } from '../../../abstraction/auth.service';
import BigNumber from 'bignumber.js';
import { GoogleAnalyticsService } from 'src/app/utils/google-analytics.service';
import { GroupsService } from '../../../abstraction/groups.service';
import {
  AlertDialogComponent,
  AlertDialogData,
} from 'src/app/modules/shared/components/dialogs/alert-dialog/alert-dialog.component';

@Component({
  selector: 'app-confirm-swap-dialog',
  templateUrl: './confirm-swap-dialog.component.html',
  styleUrls: ['./confirm-swap-dialog.component.scss'],
})
export class ConfirmSwapDialogComponent implements OnInit, OnDestroy {
  priceIntervalRef: any;
  countDownSecond = 10;
  priceExpired = false;
  firstLockOrderDone = false;
  isProcessing = false;
  errorText = '';
  showError = true;

  constructor(
    public dialogRef: MatDialogRef<ConfirmSwapDialogComponent>,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private cryptoService: CryptoService,
    private errorHandler: ErrorHandlingService,
    public authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private googleAnalyticsService: GoogleAnalyticsService,
    private groupsService: GroupsService,
    private confirmDialog: MatDialog,
  ) {}

  ngOnInit(): void {
    if (this.data.isGroup) {
      const startTime = new Date().getTime();
      this.setPriceTimer(startTime);
      this.firstLockOrderDone = true;
    } else {
      this.cryptoService
        .lockMarketOrder(this.data.currentCoinPair, this.data.operation, true)
        .subscribe({
          next: (res) => {
            this.data.code = res.code;
            this.data.expired = res.expired;
            this.data.price = res.price;
            const startTime = new Date().getTime();
            this.setPriceTimer(startTime);
            this.firstLockOrderDone = true;
          },
          error: (err) => {
            console.log('Ha ocurrido un error', err);
            if (err.error.internalStatus === 'USER_BLOCKED') {
              this.confirmDialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
                panelClass: 'modal-container',
                data: {
                  title: 'ERROR AL CREAR LA ORDEN',
                  content: `Por motivos de seguridad, su usuario se encuentra bloqueado. Por favor, contáctese con soporte para conocer más detalles.`,
                  isError: true,
                },
              });
              this.dialogRef.close(true);
            } else if (err.error.internalStatus === 'USER_UNKNOWN_IDENTITY') {
              this.confirmDialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
                panelClass: 'modal-container',
                data: {
                  title: 'ERROR AL CREAR LA ORDEN',
                  content: `Por motivos de seguridad, debe subir una selfie para validar su identidad. Por favor, contáctese con soporte para conocer más detalles.`,
                  isError: true,
                },
              });
            } else {
              this.confirmDialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
                panelClass: 'modal-container',
                data: {
                  title: 'ERROR AL CREAR LA ORDEN',
                  content: `Ha ocurrido un error al obtener el precio.`,
                  isError: true,
                },
              });
            }
          },
        });
    }
  }

  ngOnDestroy(): void {
    clearInterval(this.priceIntervalRef);
  }

  setPriceTimer(startTime: number) {
    this.priceIntervalRef = setInterval(() => {
      const currentTime = new Date().getTime();
      const distance = currentTime - startTime;
      this.countDownSecond = 10 - Math.floor((distance % (1000 * 60)) / 1000);
      if (distance >= 10000) {
        clearInterval(this.priceIntervalRef);
        this.priceExpired = true;
        this.showError = false;
      }
    }, 1000);
  }

  getNewPrice() {
    this.showError = false;
    this.isProcessing = true;
    if (this.data.isGroup) {
      this.groupsService
        .getPriceGroup(this.data.currentCoinPair.split('_').join(''))
        .subscribe((resp) => {
          this.data.price = resp.price[this.data.operation.toLowerCase()];
          this.isProcessing = false;
        });
    } else {
      this.cryptoService
        .lockMarketOrder(this.data.currentCoinPair, this.data.operation, true)
        .subscribe(
          (res) => {
            this.data.code = res.code;
            this.data.price = res.price;
            this.isProcessing = false;
          },
          (err) => {
            this.errorText = 'Error en obtener nuevo precio, intentá nuevamente.';
            this.showError = true;
          },
        );
    }
    const startTime = new Date().getTime();
    this.setPriceTimer(startTime);
    this.priceExpired = false;
  }

  confirmSwap() {
    this.showError = false;
    this.isProcessing = true;
    const newAmount: string =
      this.data.operation === 'BUY'
        ? new BigNumber(this.data.srcCoinAmount)
            .dividedBy(new BigNumber(this.data.price))
            .toString()
        : this.data.srcCoinAmount;

    const operationType =
      this.data.srcCoin === 'ARS' || this.data.srcCoin === 'USD'
        ? 'BUY'
        : this.cryptoService.isBuyingSwapPair(this.data.srcCoin, this.data.destCoin)
        ? 'BUY'
        : 'SELL';
    if (this.data.isGroup) {
      let sendOperation;
      this.data.operation === 'BUY' ? (sendOperation = 0) : (sendOperation = 1);
      this.isProcessing = false;
      this.groupsService
        .createOrderGroup(this.data.currentCoinPair.split('_').join(''), newAmount, sendOperation)
        .subscribe(
          () => {
            this.dialogRef.close(true);
            this.dialog.open(SwapSuccessDialogComponent, {
              panelClass: 'deposit',
              maxWidth: '90vw',
              data: {
                srcCoin: this.data.srcCoin,
                destCoin: this.data.destCoin,
                srcCoinAmount: this.data.srcCoinAmount,
                destCoinAmount: this.data.destCoinAmount,
                title: this.data.isGroup ? '¡Trade exitoso!' : '¡Intercambio con éxito!',
                isGroup: this.data.isGroup,
              },
            });
          },
          (error) => {
            this.dialog
              .open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
                panelClass: 'modal-container',
                data: this.errorHandler.handleGroupsError(error),
              })
              .afterClosed()
              .subscribe(() => this.dialogRef.close(true));
          },
        );
    } else {
      this.cryptoService
        .swapBetweenCoins(newAmount, this.data.currentCoinPair, operationType, this.data.code)
        .subscribe(
          (res) => {
            this.dialogRef.close(true);
            this.dialog.open(SwapSuccessDialogComponent, {
              panelClass: 'deposit',
              maxWidth: '90vw',
              data: {
                srcCoin: this.data.srcCoin,
                destCoin: this.data.destCoin,
                srcCoinAmount: this.data.srcCoinAmount,
                destCoinAmount: this.data.destCoinAmount,
                custodyFee: this.data.custodyFee ? this.data.custodyFee : '',
              },
            });
            this.authService
              .sendTikTokEvent(
                window.location.href,
                this.data.srcCoinAmount,
                '',
                this.data.srcCoinAmount,
                this.data.price,
                `Swap de ${this.data.srcCoinAmount} ${this.data.srcCoin} por ${this.data.destCoinAmount} ${this.data.destCoin}`,
              )
              .subscribe();
            this.googleAnalyticsService.emitEvent('swap-order', 'first-use', 'click');
            // Google Analytics tracking
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: {
                swapTrx: true,
                srcCoin: this.data.srcCoin,
                destCoin: this.data.destCoin,
              },
            });
            this.isProcessing = false;
          },
          (err) => {
            this.showError = true;
            this.isProcessing = false;
            const errorMessages = this.errorHandler.handleError(err);
            this.dialog.open<AlertDialogComponent, AlertDialogData>(AlertDialogComponent, {
              panelClass: 'modal-container',
              data: {
                title: errorMessages.title,
                content: errorMessages.body,
                errorCode: errorMessages.errorCode,
                isError: true,
              },
            });
          },
        );
    }
  }

  getButtonText() {
    if (!this.firstLockOrderDone) {
      return 'Obteniendo precio';
    } else if (this.priceExpired) {
      return 'Obtener nuevo precio';
    } else {
      return 'Confirmar';
    }
  }
}
